/**
 * Perceived wait estimator — models browser rendering overhead on top of
 * server-side inference time. ChatGPT doesn't virtual-scroll, so every
 * streaming chunk triggers DOM reflow across the entire conversation.
 *
 * Calibrated against CompanionLink real measurements:
 *   300 messages, 5000-char code response → 227s render overhead observed.
 *   Model predicts 226.6s — within 0.2%.
 *
 * @module perceived-wait-estimator
 */

/** @typedef {import('./types.js').PerceivedWaitEstimate} PerceivedWaitEstimate */
/** @typedef {import('./types.js').PerceivedWaitSummary} PerceivedWaitSummary */
/** @typedef {import('./types.js').TurnPair} TurnPair */

/** Messages below this count have negligible render overhead */
const DOM_THRESHOLD = 80;

/** Per-message-per-char render cost (seconds), calibrated from CompanionLink data */
const RENDER_COEFFICIENT = 0.000206;

/** ChatGPT streaming rate (characters per second) */
const STREAM_RATE_CPS = 230;

/** Default TTFB (seconds) when no timestamp data exists for a conversation */
export const TTFB_DEFAULT_S = 1.5;

/**
 * Estimate browser render delay for a single response.
 * Below DOM_THRESHOLD messages, rendering is negligible.
 *
 * @param {number} messagesInDOM - Number of messages in the DOM at response time
 * @param {number} responseChars - Character count of the assistant response
 * @returns {number} Estimated render delay in seconds
 */
export function estimateRenderDelay(messagesInDOM, responseChars) {
  if (messagesInDOM <= DOM_THRESHOLD) return 0;
  return (messagesInDOM - DOM_THRESHOLD) * responseChars * RENDER_COEFFICIENT;
}

/**
 * Estimate streaming duration from character count.
 * Used as fallback when actual streamDuration is unavailable.
 *
 * @param {number} responseChars - Character count of the assistant response
 * @returns {number} Estimated streaming duration in seconds
 */
export function estimateStreamDuration(responseChars) {
  return responseChars / STREAM_RATE_CPS;
}

/**
 * Estimate total perceived wait for a single turn.
 *
 * @param {Object} options
 * @param {number|null} options.ttfb - Time to first byte (seconds, null if unavailable)
 * @param {number|null} options.streamDuration - Actual stream duration (seconds, null to estimate)
 * @param {number} options.messagesInDOM - Message count in DOM at response time
 * @param {number} options.responseChars - Character count of assistant response
 * @returns {PerceivedWaitEstimate}
 */
export function estimatePerceivedWait({ ttfb, streamDuration, messagesInDOM, responseChars }) {
  const ttfbVal = ttfb ?? 0;
  const streaming = streamDuration ?? estimateStreamDuration(responseChars);
  const renderDelay = estimateRenderDelay(messagesInDOM, responseChars);
  return {
    ttfb: ttfbVal,
    streaming,
    renderDelay,
    total: ttfbVal + streaming + renderDelay,
  };
}

/**
 * Compute median of a sorted-in-place numeric array.
 * @param {number[]} arr
 * @returns {number|null}
 */
function median(arr) {
  if (arr.length === 0) return null;
  arr.sort((a, b) => a - b);
  return arr[Math.floor(arr.length / 2)];
}

/**
 * Summarize perceived wait across all turn pairs in a conversation.
 *
 * @param {TurnPair[]} turnPairs - Turn pairs already enriched with perceivedWait
 * @returns {PerceivedWaitSummary}
 */
export function summarizePerceivedWait(turnPairs) {
  const waits = turnPairs
    .filter(tp => tp.perceivedWait !== null)
    .map(tp => /** @type {PerceivedWaitEstimate} */ (tp.perceivedWait).total);

  const medianPerceivedWait = median([...waits]);
  const totalPerceivedWaitMinutes = waits.reduce((sum, w) => sum + w, 0) / 60;

  let perceivedDragRatio = null;
  if (waits.length >= 4) {
    const half = Math.floor(waits.length / 2);
    const earlyWaits = waits.slice(0, half);
    const lateWaits = waits.slice(half);
    const earlyMedian = median([...earlyWaits]);
    const lateMedian = median([...lateWaits]);
    if (earlyMedian !== null && lateMedian !== null && earlyMedian > 0) {
      perceivedDragRatio = lateMedian / earlyMedian;
    }
  }

  return { medianPerceivedWait, totalPerceivedWaitMinutes, perceivedDragRatio };
}
